"use client";

import { useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { normalizeDomainInput } from "@/lib/free-snapshot/domain";
import { safeFetchPublic, HttpError } from "@/lib/api/safe-fetch";
import { SnapshotStartResponse } from "@ripenn/api-contracts/webapp/snapshot";
import { cn } from "@/lib/utils";

function track(event: string, props?: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  const ph = window.posthog;
  if (ph?.capture) {
    ph.capture(event, { ...props, source: "homepage_snapshot_funnel" });
  }
}

export function FreeSnapshotForm() {
  const router = useRouter();
  const inputRef = useRef<HTMLInputElement>(null);
  const [domain, setDomain] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shake, setShake] = useState(false);

  const flagInvalid = (message: string) => {
    setError(message);
    setShake(true);
    inputRef.current?.focus();
    window.setTimeout(() => setShake(false), 400);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    const trimmed = domain.trim();
    if (!trimmed) {
      flagInvalid("Enter a domain to continue.");
      return;
    }
    if (!normalizeDomainInput(trimmed)) {
      flagInvalid("Enter a valid domain like yourdomain.com.");
      return;
    }

    setError(null);
    setSubmitting(true);
    try {
      const data = await safeFetchPublic(
        SnapshotStartResponse,
        "/api/public/snapshot/start",
        {
          method: "POST",
          body: JSON.stringify({ domain: trimmed }),
        },
      );
      track("snapshot_started", {
        domain: data.domain ?? trimmed,
        normalized_domain: data.normalizedDomain,
        has_competitor: false,
      });
      router.push(`/snapshot/${data.snapshotRunId}`);
    } catch (err) {
      flagInvalid(
        err instanceof HttpError ? err.message : "Could not start snapshot.",
      );
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-xl px-2">
      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-3 sm:flex-row sm:items-stretch"
        noValidate
      >
        <Label htmlFor="free-snapshot-domain" className="sr-only">
          Website domain
        </Label>
        <Input
          ref={inputRef}
          id="free-snapshot-domain"
          name="domain"
          type="text"
          inputMode="url"
          autoComplete="url"
          placeholder="yourdomain.com"
          value={domain}
          onChange={(ev) => {
            setDomain(ev.target.value);
            if (error) setError(null);
          }}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? "free-snapshot-error" : undefined}
          className={cn(
            "h-11 flex-1 border-border/80 bg-background/80",
            error && "border-destructive focus-visible:ring-destructive/40",
            shake && "animate-shake",
          )}
          disabled={submitting}
        />
        <Button
          type="submit"
          size="lg"
          disabled={submitting}
          className="h-11 shrink-0 px-6 text-sm"
        >
          {submitting ? (
            "Starting…"
          ) : (
            <>
              Run free snapshot
              <ArrowRight className="ml-2 h-4 w-4" />
            </>
          )}
        </Button>
      </form>
      {error && (
        <p
          id="free-snapshot-error"
          className="text-destructive mt-3 text-center text-sm"
          role="alert"
        >
          {error}
        </p>
      )}
      <p className="text-muted-foreground mt-3 text-center text-sm">
        No signup required
      </p>
    </div>
  );
}
